import { fullNamePartsSchema, phoneSchema } from '../../../core/primitives/schema';
import type { RegistrationReason } from '../../../core/identity/accountLink';

/**
 * Validation for the self-report form.
 *
 * Pure, so the rules a person is held to can be tested without rendering
 * anything (§5.4). The screen renders the result; it does not decide it.
 */

export interface RegistrationFormValues {
  readonly firstName: string;
  readonly fatherName: string;
  readonly grandfatherName: string;
  readonly familyName: string;
  readonly phone: string;
  readonly branchId: string;
}

export const EMPTY_REGISTRATION_FORM: RegistrationFormValues = {
  firstName: '',
  fatherName: '',
  grandfatherName: '',
  familyName: '',
  phone: '',
  branchId: '',
};

export type RegistrationFieldErrors = Partial<Record<keyof RegistrationFormValues, string>>;

export interface RegistrationValidation {
  readonly errors: RegistrationFieldErrors;
  readonly valid: boolean;
}

/**
 * Validates every field and reports all failures at once.
 *
 * Stopping at the first error makes someone fix one thing, submit, and be told
 * about the next — which on a phone, over a slow connection, is how a form gets
 * abandoned.
 */
export function validateRegistrationForm(
  values: RegistrationFormValues,
): RegistrationValidation {
  const errors: RegistrationFieldErrors = {};

  const nameParts = fullNamePartsSchema.safeParse({
    firstName: values.firstName,
    fatherName: values.fatherName,
    grandfatherName: values.grandfatherName,
    familyName: values.familyName,
  });

  if (!nameParts.success) {
    for (const issue of nameParts.error.issues) {
      const field = issue.path[0];
      if (typeof field === 'string' && field in EMPTY_REGISTRATION_FORM) {
        errors[field as keyof RegistrationFormValues] ??= issue.message;
      }
    }
  }

  const phone = phoneSchema.safeParse(values.phone);
  if (!phone.success) {
    errors.phone = phone.error.issues[0]?.message ?? 'رقم الجوال غير صحيح';
  }

  if (!values.branchId) {
    errors.branchId = 'اختر الفرع';
  }

  return { errors, valid: Object.keys(errors).length === 0 };
}

/** Shapes validated values into what the port expects. */
export function toRegistrationDraft(
  values: RegistrationFormValues,
  reason: RegistrationReason,
  mismatchedEmployeeId?: string,
) {
  return {
    reason,
    fullName: {
      firstName: values.firstName.trim(),
      fatherName: values.fatherName.trim(),
      grandfatherName: values.grandfatherName.trim(),
      familyName: values.familyName.trim(),
    },
    // Normalised by the schema, so a number typed with a country code or
    // spaces is stored the same way as one typed bare.
    phone: phoneSchema.parse(values.phone),
    branchId: values.branchId,
    ...(mismatchedEmployeeId === undefined ? {} : { mismatchedEmployeeId }),
  };
}
